import { FC, useMemo } from "react";
import { Category } from "@/types/category";
import ChevronLeft from "@/assets/images/ChevronLeft";
import styles from "./SelectProduct.module.scss";
import { getTextFromTranslations } from "@/helpers/translation";
import { ProductCard } from "@/components/product-card";
import { Product } from "@/types/product";
import { useTranslation } from "react-i18next";
import { logFirebaseEvent } from "@/services/firebase";
import { playSound, SoundOption } from "@/helpers/playSound";
import { useAppSelector } from "@/hooks/useAppSelector";
import { makeSelectVisibleProductIds } from "@/store/product/selectors";

interface SelectProductProps {
  category: Category | null;
  filteredProducts: Product[] | null;
  onGoBack: () => void;
  onProductPress: (product: Product) => void;
}

const SelectProduct: FC<SelectProductProps> = ({
  category,
  filteredProducts,
  onGoBack,
  onProductPress,
}) => {
  const { t, i18n } = useTranslation();
  const categoryName = getTextFromTranslations(category?.translations)?.name;
  const selectProductIds = useMemo(() => makeSelectVisibleProductIds(), []);
  const productIds = useAppSelector((state) =>
    selectProductIds(state, {
      sortLanguage: i18n.language,
      category: category ? category : undefined,
    })
  );

  const onClick = (product: Product | undefined) => {
    if (!product) return;
    playSound(SoundOption.Click1);
    onProductPress(product);
    logFirebaseEvent("product_card_pressed", {
      id: product.id,
      name: product.translations[0].name,
    });
  };

  const visibleProductIds = filteredProducts ? filteredProducts.map((p) => p.id) : productIds;

  return (
    <>
      <div className={styles.breadcrumbs} onClick={onGoBack}>
        {categoryName && <ChevronLeft />}
        <h3 className="h4">{categoryName ?? t("mainPage.foundProducts")}</h3>
      </div>
      <div className={styles.gridWrapper} data-cy="select-product-grid">
        <div className={styles.grid}>
          {visibleProductIds.map((productId) => (
            <ProductCard key={productId} onClick={onClick} productId={productId} />
          ))}
        </div>
      </div>
    </>
  );
};

export default SelectProduct;
